/**
 * A palette-index pixel buffer with a hard-edged line rasterizer — the map's
 * drawing surface.
 *
 * The shell's own draw list (`shell/render/draw-ops.ts`) has no line primitive,
 * because nothing else in the original's UI needed one: every raised panel,
 * button and well is axis-aligned rectangles. A map is the opposite — a few
 * hundred road and river runs at arbitrary angles — and expressing those as
 * one-pixel `fill` ops would put a few thousand objects through the draw list
 * per frame. So the map keeps the shell's *colour model* (palette indices, never
 * CSS colours, resolved against `PS.PAL` only at the very end) and brings its
 * own rasterizer.
 *
 * Bresenham rather than a canvas `stroke()` on purpose: a canvas stroke is
 * antialiased, and a soft grey fringe on a 320×200 frame that is then blown up
 * with nearest-neighbour reads as a blurry modern map. The original's own
 * line-drawing was integer and hard-edged, and so is this.
 *
 * Pure — no DOM, no canvas — so a headless test can rasterize a map and count
 * pixels of a given palette index, which is how this lane's tests check that
 * the network, the front line and the player marker actually reached the frame.
 */

import type { IndexedPalette } from "../shell/render/ps-palette.ts";

export interface IndexRaster {
  readonly width: number;
  readonly height: number;
  /** One palette index per pixel, row-major. */
  /** `Uint16Array`, not 8 — the sheet's synthetic inks live at index 256 and up. */
  readonly pixels: Uint16Array;
}

export function createRaster(width: number, height: number, fillIndex: number): IndexRaster {
  const pixels = new Uint16Array(Math.max(0, width) * Math.max(0, height));
  pixels.fill(fillIndex);
  return { width, height, pixels };
}

export function plot(raster: IndexRaster, x: number, y: number, index: number): void {
  const px = Math.round(x);
  const py = Math.round(y);
  if (px < 0 || py < 0 || px >= raster.width || py >= raster.height) return;
  raster.pixels[py * raster.width + px] = index;
}

export function pixelAt(raster: IndexRaster, x: number, y: number): number | undefined {
  if (x < 0 || y < 0 || x >= raster.width || y >= raster.height) return undefined;
  return raster.pixels[y * raster.width + x];
}

/**
 * Integer Bresenham between two points, endpoints included. Coordinates are
 * rounded once up front rather than per step, so a line's two ends land on the
 * same pixels `plot` would give them on their own — which is what keeps a
 * polyline's joints closed.
 */
export function line(raster: IndexRaster, x0: number, y0: number, x1: number, y1: number, index: number): void {
  let x = Math.round(x0);
  let y = Math.round(y0);
  const endX = Math.round(x1);
  const endY = Math.round(y1);
  const dx = Math.abs(endX - x);
  const dy = -Math.abs(endY - y);
  const stepX = x < endX ? 1 : -1;
  const stepY = y < endY ? 1 : -1;
  let error = dx + dy;
  // Bounded by the buffer's own diagonal: a caller passing a wildly
  // out-of-range endpoint (a waypoint outside the region, say) must cost a
  // clipped line, not a hang.
  const limit = raster.width + raster.height + Math.abs(dx) + Math.abs(dy);
  for (let step = 0; step <= limit; step += 1) {
    plot(raster, x, y, index);
    if (x === endX && y === endY) return;
    const doubled = 2 * error;
    if (doubled >= dy) {
      error += dy;
      x += stepX;
    }
    if (doubled <= dx) {
      error += dx;
      y += stepY;
    }
  }
}

/** A dashed run — the map's idiom for anything approximate or notional (the front line, an inferred aerodrome position). `on`/`off` are pixel counts along the line. */
export function dashedLine(
  raster: IndexRaster,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  index: number,
  on = 3,
  off = 3,
): void {
  const span = Math.hypot(x1 - x0, y1 - y0);
  const steps = Math.max(1, Math.ceil(span));
  const period = Math.max(1, on + off);
  for (let step = 0; step <= steps; step += 1) {
    if (step % period >= on) continue;
    const t = step / steps;
    plot(raster, x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, index);
  }
}

/** The four sides of a rectangle, one pixel wide. */
export function rectOutline(raster: IndexRaster, x: number, y: number, w: number, h: number, index: number): void {
  if (w <= 0 || h <= 0) return;
  line(raster, x, y, x + w - 1, y, index);
  line(raster, x, y + h - 1, x + w - 1, y + h - 1, index);
  line(raster, x, y, x, y + h - 1, index);
  line(raster, x + w - 1, y, x + w - 1, y + h - 1, index);
}

/** A filled axis-aligned block — used for the marker cores, which are 1–3 px squares. */
export function fillRect(raster: IndexRaster, x: number, y: number, w: number, h: number, index: number): void {
  for (let row = 0; row < h; row += 1) {
    for (let column = 0; column < w; column += 1) plot(raster, x + column, y + row, index);
  }
}

/** A midpoint-algorithm circle outline — the aerodrome ring and the hill contour. */
export function circleOutline(raster: IndexRaster, centreX: number, centreY: number, radius: number, index: number): void {
  let x = Math.round(radius);
  let y = 0;
  let error = 1 - x;
  while (x >= y) {
    for (const [dx, dy] of [
      [x, y],
      [y, x],
      [-x, y],
      [-y, x],
      [x, -y],
      [y, -x],
      [-x, -y],
      [-y, -x],
    ]) {
      plot(raster, centreX + (dx ?? 0), centreY + (dy ?? 0), index);
    }
    y += 1;
    if (error < 0) error += 2 * y + 1;
    else {
      x -= 1;
      error += 2 * (y - x) + 1;
    }
  }
}

/**
 * Resolves the raster's indices against a palette into a `putImageData`-ready
 * buffer — the single point where an index becomes a colour, matching
 * `shell/render/canvas-painter.ts`'s rule for the rest of the shell. Every
 * pixel is opaque: the map fills its own well before drawing into it.
 */
/**
 * A horizontal-scanline fill of an arbitrary polygon, even-odd rule, endpoints
 * treated as a closed ring. The map has no other use for a filled shape than
 * water — the sea plates are 40–93 triangles apiece — and a triangle is just a
 * three-point polygon, so this stays general rather than special-casing one.
 *
 * The half-open `y + 0.5` sample and the `>` / `<=` edge test together are the
 * usual top-left rule written for a scalar rasterizer: an edge is counted only
 * where the scanline is strictly inside its vertical span, so two triangles
 * sharing an edge neither double-paint it nor leave a seam between them.
 */
export function fillPolygon(raster: IndexRaster, points: readonly { readonly x: number; readonly y: number }[], index: number): void {
  if (points.length < 3) return;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    if (point.y < minY) minY = point.y;
    if (point.y > maxY) maxY = point.y;
  }
  const firstRow = Math.max(0, Math.ceil(minY - 0.5));
  const lastRow = Math.min(raster.height - 1, Math.floor(maxY - 0.5));
  const crossings: number[] = [];

  for (let row = firstRow; row <= lastRow; row += 1) {
    const scanY = row + 0.5;
    crossings.length = 0;
    for (let at = 0; at < points.length; at += 1) {
      const a = points[at]!;
      const b = points[(at + 1) % points.length]!;
      const low = Math.min(a.y, b.y);
      const high = Math.max(a.y, b.y);
      if (scanY <= low || scanY > high) continue;
      crossings.push(a.x + ((scanY - a.y) / (b.y - a.y)) * (b.x - a.x));
    }
    crossings.sort((left, right) => left - right);
    for (let pair = 0; pair + 1 < crossings.length; pair += 2) {
      const from = Math.max(0, Math.ceil(crossings[pair]! - 0.5));
      const to = Math.min(raster.width - 1, Math.floor(crossings[pair + 1]! - 0.5));
      for (let column = from; column <= to; column += 1) raster.pixels[row * raster.width + column] = index;
    }
  }
}

/**
 * The sheet's synthetic inks, one virtual index each past `PS.PAL`'s 256.
 *
 * `PS.PAL`'s shared 224–255 block — the one `shell/render/ps-palette.ts`
 * establishes is identical across every screen palette in the game — is a warm
 * tan ramp, a burnt-orange ramp and one grey. It is a sepia map's whole
 * inkwell, and it has **no blue and no green at all**, so the classes a
 * legible map needs those for cannot be drawn from it. Rather than abandon the
 * index model and smuggle CSS colours into the painter, the sheet keeps
 * trafficking only in indices and resolves these four here, at the same single
 * point where every other index becomes a colour.
 *
 * Blue water and green woods over buff paper is what a period printed map
 * actually looked like, so the extension is in idiom as well as legible. The
 * player's orange and the target's red were chosen by the user (2026-08-23).
 */
export const MARKER_ORANGE_INDEX = 256;
/** Rivers, canals and the coastline, as a printed map inks moving water. */
export const WATER_LINE_INDEX = 257;
/** The sea itself: a pale wash, light enough that the grid, the towns and the route all still read over it. */
export const WATER_FILL_INDEX = 258;
/** Woods, heath and the landcover plates' boundaries — muted enough to sit behind the roads. */
export const WOOD_GREEN_INDEX = 259;

const SYNTHETIC_INKS: ReadonlyMap<number, readonly [number, number, number]> = new Map<number, readonly [number, number, number]>([
  [MARKER_ORANGE_INDEX, [235, 100, 16]],
  [WATER_LINE_INDEX, [42, 92, 148]],
  [WATER_FILL_INDEX, [150, 190, 214]],
  [WOOD_GREEN_INDEX, [92, 128, 74]],
]);

export function rasterToImageData(raster: IndexRaster, palette: IndexedPalette): ImageData {
  const image = new ImageData(Math.max(1, raster.width), Math.max(1, raster.height));
  for (let i = 0; i < raster.pixels.length; i += 1) {
    const index = raster.pixels[i] ?? 0;
    const colour = SYNTHETIC_INKS.get(index) ?? palette.colors[index] ?? [0, 0, 0];
    image.data[i * 4] = colour[0];
    image.data[i * 4 + 1] = colour[1];
    image.data[i * 4 + 2] = colour[2];
    image.data[i * 4 + 3] = 255;
  }
  return image;
}
